import { Fragment, memo, type ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentStep, getSteps } from "~/features/configurator/Linear/redux/selectors";

import { goToStep } from "~/features/configurator/Linear/redux/actions";
import { useRenderStepName } from "~/features/configurator/common/useRenderStepName";
import { styled, Typography } from "~/common/mui";
import EditIcon from "@mui/icons-material/Edit";

const STEP_WIDTH = 18.75;
const CIRCLE_RADIUS = 3.75;
const CIRCLE_SAFE_AREA = 1;

const StepsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridAutoFlow: "column",
  gridAutoColumns: `minmax(auto, ${theme.spacing(STEP_WIDTH)}) 1fr`,
  width: "100%",
}));

const StepDivider = styled("div")(({ theme }) => ({
  height: theme.spacing(CIRCLE_RADIUS),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& > hr": {
    flex: `0 0 calc(100% + ${theme.spacing(STEP_WIDTH - CIRCLE_RADIUS - CIRCLE_SAFE_AREA * 2)})`,
    margin: 0,
    borderColor: theme.palette.divider,
  },
}));

type StepState = "default" | "active" | "done" | "disabled";

const StepNumber = styled("div", {
  shouldForwardProp: (prop) => prop !== "stepState",
})<{ stepState: StepState }>(({ stepState, theme }) => ({
  margin: theme.spacing(0, 2.5625),
  width: theme.spacing(CIRCLE_RADIUS),
  height: theme.spacing(CIRCLE_RADIUS),
  lineHeight: theme.spacing(CIRCLE_RADIUS),
  borderRadius: "50%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: theme.palette.secondary.contrastText,
  backgroundColor:
    stepState === "active" || stepState === "done"
      ? theme.palette.primary.main
      : stepState === "disabled"
        ? theme.palette.action.disabled
        : theme.palette.secondary.main,
}));

const StepRoot = styled("div", {
  shouldForwardProp: (prop) => prop !== "stepState",
})<{ stepState: StepState }>(({ stepState, theme }) => ({
  position: "relative",
  cursor: stepState === "disabled" ? "default" : "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  "&:hover .step-number": {
    backgroundColor:
      stepState === "active" || stepState === "done"
        ? theme.palette.primary.onContainer
        : stepState === "disabled"
          ? undefined
          : theme.palette.secondary.onContainer,
  },
}));

export const LinearStepNavigation = memo(() => {
  const currentStep = useSelector(getCurrentStep);
  const stepsList = useSelector(getSteps);

  const renderStepName = useRenderStepName();

  return (
    <StepsGrid>
      {stepsList.map((step, index) => {
        const stepState: StepState = !step.active
          ? "disabled"
          : index === currentStep
            ? "active"
            : index < currentStep
              ? "done"
              : "default";

        return (
          <Fragment key={index}>
            <Step
              name={renderStepName(index)}
              index={index}
              stepState={stepState}
              enabled={step.active}
            />
            {stepsList.length - 1 !== index && (
              <StepDivider>
                <hr />
              </StepDivider>
            )}
          </Fragment>
        );
      })}
    </StepsGrid>
  );
});

interface StepProps {
  name: ReactNode;
  index: number;
  stepState: StepState;
  enabled?: boolean;
}

const Step = (props: StepProps) => {
  const { name, index, stepState, enabled } = props;

  const dispatch = useDispatch();
  const clickHandler = () => {
    dispatch(goToStep(index));
  };

  const content =
    enabled && stepState === "done" ? <EditIcon  /> : index + 1;

  return (
    <StepRoot stepState={stepState} onClick={enabled ? clickHandler : undefined}>
      <StepNumber stepState={stepState} className="step-number">
        {content}
      </StepNumber>
      <Typography
        variant="labelLarge"
        sx={{
          paddingTop: 0.625,
          bottom: 0,
          textAlign: "center",
          ...(stepState === "disabled" && { color: "action.disabled" }),
        }}
      >
        {name}
      </Typography>
    </StepRoot>
  );
};
